import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, Globe } from "lucide-react";
import { useLang } from "../i18n";
import { useSettings } from "../settings";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { lang, setLang, t } = useLang();
  const { settings } = useSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const nav = [
    { label: t("nav.kurumsal"), to: "/kurumsal" },
    { label: t("nav.sektorler"), to: "/sektorler" },
    { label: t("nav.urunler"), to: "/urunlerimiz" },
    { label: t("nav.dokumanlar"), to: "/dokumanlar" },
    { label: t("nav.sosyal"), to: "/sosyal-sorumluluk" },
    { label: t("nav.haberler"), to: "/haberler" },
    { label: t("nav.iletisim"), to: "/iletisim" },
  ];

  const toggleLang = () => setLang(lang === "tr" ? "en" : "tr");

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,box-shadow,padding] duration-300 bg-[#4E0D1C] ${
        scrolled ? "shadow-[0_2px_18px_rgba(78,13,28,0.35)] py-3" : "py-4 md:py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between gap-3 md:gap-6">
        <Link to="/" className="flex items-center gap-3 shrink-0" aria-label={settings.legal_name}>
          {settings.logo_url ? (
            <img
              src={settings.logo_url}
              alt={settings.brand_name}
              className="h-10 md:h-11 w-auto max-w-[200px] object-contain"
              loading="eager"
              fetchpriority="high"
              width="200"
              height="44"
            />
          ) : (
            <div className="h-10 md:h-11 w-[160px]" aria-hidden="true" />
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 bg-white/10 rounded-full px-1.5 py-1 border border-white/15 backdrop-blur">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `px-3 py-2 text-[14px] font-medium rounded-full transition-colors duration-200 whitespace-nowrap ${
                  isActive
                    ? "text-[#4E0D1C] bg-[#C9A24B]"
                    : "text-white/85 hover:text-white hover:bg-white/10"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleLang}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors border border-white/15"
            aria-label="Language"
          >
            <Globe className="w-4 h-4" />
            <span>{lang === "tr" ? "TR" : "EN"}</span>
          </button>
          <a
            href={`tel:${(settings.phone || "").replace(/\s+/g, "")}`}
            className="w-11 h-11 rounded-full bg-[#C9A24B] hover:bg-white text-[#4E0D1C] flex items-center justify-center transition-colors duration-200 shadow-[0_4px_14px_rgba(201,162,75,0.35)]"
            aria-label={t("common.contactUs")}
          >
            <Phone className="w-5 h-5" />
          </a>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors border border-white/15"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          open ? "max-h-[460px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 md:px-6 pb-6 pt-2 bg-[#4E0D1C] border-t border-white/10">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `block py-3 text-base font-medium border-b border-white/10 ${
                  isActive ? "text-[#C9A24B]" : "text-white/90"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <button
            onClick={toggleLang}
            className="mt-4 inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white/10 text-white text-sm font-semibold border border-white/15"
          >
            <Globe className="w-4 h-4" />
            {lang === "tr" ? "Türkçe" : "English"}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
